import { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  InputAdornment,
  MenuItem,
  Stack,
  Fade,
  CircularProgress,
  List,
  ListItemText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import CakeIcon from '@mui/icons-material/Cake';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import TransgenderIcon from '@mui/icons-material/Transgender';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import TagIcon from '@mui/icons-material/Tag';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { systemAPI, patientAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { normalizeGreekSearch } from '../utils/greekUtils';

const defaultValues = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  cardDate: '',
  gender: '',
  phone: '',
  email: '',
  amka: '',
  am: '',
  address: '',
  diagnosis: [],
  notes: '',
  age: '',
};

const validationSchema = (t) => Yup.object({
  firstName: Yup.string().required(t('Required')),
  lastName: Yup.string().required(t('Required')),
  dateOfBirth: Yup.string().nullable(),
  cardDate: Yup.string().nullable(),
  gender: Yup.string().nullable(),
  phone: Yup.string().required(t('Required')),
  email: Yup.string().email(t('Invalid email')).nullable(),
  amka: Yup.string()
    .test('is-11-digits', t('AMKA must be exactly 11 digits'), (val) => !val || /^[0-9]{11}$/.test(val))
    .nullable(),
  am: Yup.string().nullable(),
  address: Yup.string().nullable(),
  diagnosis: Yup.mixed().nullable(),
  notes: Yup.string().nullable(),
});

const calculateAge = (dobString) => {
  if (!dobString) return '';
  const dob = new Date(dobString);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 ? age.toString() : '';
};

const extractDobFromAmka = (amka) => {
  if (!amka || amka.length < 6) return null;
  const day = amka.substring(0, 2);
  const month = amka.substring(2, 4);
  const yearShort = parseInt(amka.substring(4, 6), 10);
  
  const currentYearLong = new Date().getFullYear();
  const currentYearShort = currentYearLong % 100;
  
  const fullYear = (yearShort > currentYearShort) ? 1900 + yearShort : 2000 + yearShort;
  
  // Validate basic date parts
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  if (d < 1 || d > 31 || m < 1 || m > 12) return null;

  return `${fullYear}-${month}-${day}`;
};

const IMPORTANT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#d32f2f',
      borderWidth: '1.5px',
    },
    '&:hover fieldset': {
      borderColor: '#c62828',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#d32f2f',
      borderWidth: '2px',
    },
  },
};

const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
    <Icon color="primary" sx={{ fontSize: '1.2rem' }} />
    <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 1.2 }}>
      {title}
    </Typography>
    <Divider sx={{ flex: 1, ml: 1, opacity: 0.6 }} />
  </Box>
);

const PatientEditDialog = ({ open, patient, onClose, onSubmit, onDelete, submitting, error }) => {
  const { t } = useTranslation();
  const [epembaseisOptions, setEpembaseisOptions] = useState([]);
  const [ageManuallySet, setAgeManuallySet] = useState(false);

  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  useEffect(() => {
    if (open) {
      setAgeManuallySet(false); // Reset on open
      setDuplicates([]); // Reset duplicates
      systemAPI.getEpembaseis()
        .then(res => {
          setEpembaseisOptions(res.data.map(e => e.epembasi));
        })
        .catch(err => console.error('Failed to load epembaseis:', err));
    }
  }, [open]);

  const initialValues = useMemo(
    () => ({
      ...defaultValues,
      ...(patient
        ? {
            firstName: patient.first_name ?? '',
            lastName: patient.last_name ?? '',
            dateOfBirth: patient.date_of_birth ?? '',
            cardDate: patient.card_date ?? '',
            gender: patient.gender ?? '',
            phone: patient.phone ?? '',
            email: patient.email ?? '',
            amka: patient.amka ?? '',
            am: patient.am ?? '',
            address: patient.address ?? '',
            diagnosis: patient.diagnosis ? patient.diagnosis.split(', ').filter(Boolean) : [],
            notes: patient.notes ?? '',
            age: patient.age ?? '',
          }
        : {}),
    }),
    [patient]
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          '& .MuiFormLabel-asterisk': {
            color: '#d32f2f', // Brand red for required fields
            fontWeight: 'bold',
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3, 
        bgcolor: 'primary.main', 
        color: 'primary.contrastText',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PersonIcon />
          <Typography variant="h6" fontWeight="bold">
            {patient ? t('Edit Patient') : t('Add Patient')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {patient && (
            <Tooltip title={t('Delete')}>
              <IconButton
                color="inherit"
                onClick={() => {
                  onClose();
                  onDelete?.(patient);
                }}
                sx={{ opacity: 0.8, '&:hover': { opacity: 1, bgcolor: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton color="inherit" onClick={onClose} sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema(t)}
        onSubmit={onSubmit}
      >
        {({ values, handleChange, touched, errors, handleBlur, setFieldValue }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            const timer = setTimeout(() => {
              const hasName = values.lastName?.length > 1; // Search by last name alone now
              const hasAmka = values.amka?.length >= 11;
              const hasPhone = values.phone?.length >= 8;
              
              if (hasName || hasAmka || hasPhone) {
                setCheckingDuplicates(true);
                patientAPI.checkDuplicates({
                  amka: values.amka,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  dateOfBirth: values.dateOfBirth,
                  age: values.age,
                  phone: values.phone,
                  excludeId: patient?.id
                })
                .then(res => {
                  setDuplicates(res.data.duplicates || []);
                })
                .catch(err => console.error('Duplicate check failed:', err))
                .finally(() => setCheckingDuplicates(false));
              } else {
                setDuplicates([]);
              }
            }, 800);
            return () => clearTimeout(timer);
          }, [values.firstName, values.lastName, values.amka, values.dateOfBirth, values.age, values.phone]);

          const getFieldStyle = (fieldName) => {
            if (duplicates.length === 0) return {};
            
            const lName = normalizeGreekSearch(values.lastName || '');
            const fName = normalizeGreekSearch(values.firstName || '');
            const amka = values.amka || '';
            const phone = values.phone || '';
            const age = String(values.age || '');

            const amkaMatch = amka && duplicates.some(d => d.amka === amka);
            const phoneMatch = phone && duplicates.some(d => (d.phone === phone || d.mobile === phone));
            const lNameMatch = lName && duplicates.some(d => normalizeGreekSearch(d.last_name || '') === lName);
            const fNameMatch = lNameMatch && fName && duplicates.some(d => normalizeGreekSearch(d.last_name || '') === lName && normalizeGreekSearch(d.first_name || '').startsWith(fName));
            const ageMatch = fNameMatch && age && duplicates.some(d => normalizeGreekSearch(d.last_name || '') === lName && normalizeGreekSearch(d.first_name || '').startsWith(fName) && String(d.age) === age);

            let color = '';
            if (fieldName === 'lastName') {
              if (ageMatch) color = '#ffcdd2'; // Red light
              else if (fNameMatch) color = '#ffe0b2'; // Orange light
              else if (lNameMatch) color = '#fff9c4'; // Yellow light
            } else if (fieldName === 'firstName') {
              if (ageMatch) color = '#ffcdd2';
              else if (fNameMatch) color = '#ffe0b2';
            } else if (fieldName === 'age' && ageMatch) {
              color = '#ffcdd2';
            } else if (fieldName === 'amka' && amkaMatch) {
              color = '#ffcdd2';
            } else if (fieldName === 'phone' && phoneMatch) {
              color = '#ffcdd2';
            }

            return color ? { '& .MuiOutlinedInput-root': { backgroundColor: color } } : {};
          };

          const isSevereDuplicate = (() => {
            const amka = values.amka || '';
            const phone = values.phone || '';
            const lName = normalizeGreekSearch(values.lastName || '');
            const fName = normalizeGreekSearch(values.firstName || '');
            const age = String(values.age || '');

            return (amka && duplicates.some(d => d.amka === amka)) ||
                   (phone && duplicates.some(d => (d.phone === phone || d.mobile === phone))) ||
                   (lName && fName && age && duplicates.some(d => normalizeGreekSearch(d.last_name || '') === lName && normalizeGreekSearch(d.first_name || '').startsWith(fName) && String(d.age) === age));
          })();

          return (
            <Form>
              <DialogContent sx={{ p: 4, maxHeight: '70vh' }}>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
                
                {duplicates.length > 0 && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    action={
                      <Button color="inherit" size="small" onClick={() => setDuplicates([])}>
                        {t('Dismiss')}
                      </Button>
                    }
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('Warning: Potential Duplicate Patients Found')}
                    </Typography>
                    <List size="small" sx={{ py: 0 }}>
                      {duplicates.map(dup => (
                        <ListItemText 
                          key={dup.id} 
                          primary={
                            <Link 
                              to={`/patients/${dup.id}`} 
                              style={{ color: 'inherit', fontWeight: 'bold' }}
                              onClick={() => onClose()}
                            >
                              {dup.last_name} {dup.first_name} {dup.amka ? `(AMKA: ${dup.amka})` : ''}
                            </Link>
                          }
                          secondary={
                            <Stack direction="row" spacing={1} divider={<span>|</span>}>
                              {dup.age ? <span>{t('Age')}: {dup.age}</span> : (dup.date_birth ? <span>{t('Born')}: {new Date(dup.date_birth).toLocaleDateString()}</span> : null)}
                              {dup.phone && <span>{t('Phone')}: {dup.phone}</span>}
                            </Stack>
                          }
                        />
                      ))}
                    </List>
                  </Alert>
                )}
                <Grid container spacing={3}>
                  {/* Primary Data Row 1: Names */}
                  <Grid item xs={12} sm={6}>
                      <TextField
                        name="lastName"
                        label={t('Last Name')}
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        fullWidth
                        required
                        sx={{ ...IMPORTANT_FIELD_SX, ...getFieldStyle('lastName') }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="firstName"
                        label={t('First Name')}
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                        fullWidth
                        required
                        sx={{ ...IMPORTANT_FIELD_SX, ...getFieldStyle('firstName') }}
                      />
                  </Grid>

                  {/* Primary Data Row 2: ID & Birth */}
                  <Grid item xs={12} sm={7}>
                      <TextField
                        name="amka"
                        label={t('AMKA')}
                        value={values.amka}
                        onChange={(e) => {
                          handleChange(e);
                          const val = e.target.value;
                          if (val && val.length === 11) {
                            const dob = extractDobFromAmka(val);
                            if (dob) {
                              setFieldValue('dateOfBirth', dob);
                              const calculatedAge = calculateAge(dob);
                              if (values.age && values.age !== calculatedAge) {
                                if (window.confirm(`${t('Replace current age')} (${values.age}) ${t('with value from AMKA')} (${calculatedAge})?`)) {
                                  setFieldValue('age', calculatedAge);
                                  setAgeManuallySet(false);
                                }
                              } else if (!ageManuallySet) {
                                setFieldValue('age', calculatedAge);
                              }
                            }
                          }
                        }}
                        onBlur={handleBlur}
                        error={touched.amka && Boolean(errors.amka)}
                        helperText={touched.amka && errors.amka}
                        placeholder="12345678901"
                        fullWidth
                        sx={{ ...IMPORTANT_FIELD_SX, ...getFieldStyle('amka') }}
                        InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      name="age"
                      label={t('Age')}
                      value={values.age}
                      onChange={(e) => {
                        setAgeManuallySet(true);
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={touched.age && Boolean(errors.age)}
                      helperText={touched.age && errors.age}
                      fullWidth
                      sx={{ ...IMPORTANT_FIELD_SX, ...getFieldStyle('age') }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      name="dateOfBirth"
                      label={t('Date of Birth')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={values.dateOfBirth}
                      onChange={(e) => {
                        handleChange(e);
                        if (!ageManuallySet) {
                          setFieldValue('age', calculateAge(e.target.value));
                        }
                      }}
                      onBlur={handleBlur}
                      error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
                      helperText={touched.dateOfBirth && errors.dateOfBirth}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CakeIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Primary Data Row 3: Demographics & Registration */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      name="gender"
                      label={t('Gender')}
                      value={values.gender}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.gender && Boolean(errors.gender)}
                      helperText={touched.gender && errors.gender}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TransgenderIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      <MenuItem value="">{t('None')}</MenuItem>
                      <MenuItem value="Male">{t('Male')}</MenuItem>
                      <MenuItem value="Female">{t('Female')}</MenuItem>
                      <MenuItem value="Other">{t('Other')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="cardDate"
                      label={t('Card Date')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={values.cardDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.cardDate && Boolean(errors.cardDate)}
                      helperText={touched.cardDate && errors.cardDate}
                      fullWidth
                      sx={IMPORTANT_FIELD_SX}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="phone"
                      label={t('Phone')}
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      fullWidth
                      required
                      sx={{ ...IMPORTANT_FIELD_SX, ...getFieldStyle('phone') }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Row 4: Diagnosis (Full Width / Emphasis) */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={epembaseisOptions}
                      value={Array.isArray(values.diagnosis) ? values.diagnosis : []}
                      onChange={(e, newValue) => {
                        setFieldValue('diagnosis', newValue);
                      }}
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="diagnosis"
                          label={t('Diagnosis')}
                          error={touched.diagnosis && Boolean(errors.diagnosis)}
                          helperText={touched.diagnosis && errors.diagnosis}
                          fullWidth
                          multiline
                          minRows={1}
                          sx={IMPORTANT_FIELD_SX}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start" sx={{ ml: 1 }}>
                                  <InfoIcon fontSize="small" color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Section 2: Supplemental Information */}
                  <Grid item xs={12}>
                    <SectionHeader icon={InfoIcon} title={t('Supplemental Information')} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="email"
                      label={t('Email')}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="am"
                      label={t('AM')}
                      value={values.am}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.am && Boolean(errors.am)}
                      helperText={touched.am && errors.am}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AssignmentIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="address"
                      label={t('Address')}
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                      fullWidth
                      multiline
                      minRows={1}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="notes"
                      label={t('Notes')}
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.notes && Boolean(errors.notes)}
                      helperText={touched.notes && errors.notes}
                      fullWidth
                      multiline
                      minRows={3}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <DescriptionIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              
              <Divider />
              
              <DialogActions sx={{ p: 3, bgcolor: 'action.hover' }}>
                <Button onClick={onClose} disabled={submitting} sx={{ borderRadius: 2 }}>
                  {t('Cancel')}
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={submitting}
                  sx={{ 
                    borderRadius: 2,
                    px: 4,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                  }}
                >
                  {submitting ? <CircularProgress size={24} /> : (patient ? t('Save') : t('Add Patient'))}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default PatientEditDialog;
